import { NodeType, createInitialWorkflow, type NodeData, type WorkflowDocument, type WorkflowEdge, type WorkflowNode } from '../nodes/NodeTypes';
import { streamEdgeStyle } from './streamStyles';

export const WORKFLOW_STORAGE_KEY = 'ffmpeg-workflow-v3';
const LEGACY_NODES_KEY = 'ffmpeg-nodes';
const LEGACY_EDGES_KEY = 'ffmpeg-edges';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringParams(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, item == null ? '' : String(item)]));
}

function normalizeNode(value: unknown, index: number): WorkflowNode | null {
  if (!isRecord(value)) return null;
  const rawType = value.type;
  const type = rawType === NodeType.INPUT || rawType === NodeType.OUTPUT ? rawType : NodeType.PROCESSING;
  const rawData = isRecord(value.data) ? value.data : {};
  const params = stringParams(rawData.params);
  if (type === NodeType.INPUT && !params.filename) params.filename = String(rawData.filename || '');
  if (type === NodeType.OUTPUT && !params.filename) params.filename = String(rawData.filename || 'output.mp4');
  const streamType = rawData.streamType === 'audio' || rawData.isAudio === true ? 'audio' : rawData.streamType === 'video' ? 'video' : undefined;
  const data: NodeData = {
    label: typeof rawData.label === 'string' ? rawData.label : type === NodeType.INPUT ? '输入文件' : type === NodeType.OUTPUT ? '输出文件' : '滤镜',
    params,
    command: typeof rawData.command === 'string' ? rawData.command : undefined,
    helpText: typeof rawData.helpText === 'string' ? rawData.helpText : undefined,
    category: typeof rawData.category === 'string' ? rawData.category : undefined,
    presetId: typeof rawData.presetId === 'string' ? rawData.presetId : undefined,
    streamType,
    operation: rawData.operation === 'option' ? 'option' : type === NodeType.PROCESSING ? 'filter' : undefined,
    inputCount: Number.isFinite(Number(rawData.inputCount)) ? Math.max(1, Number(rawData.inputCount)) : 1,
    isAudio: streamType === 'audio',
    supportsTimeRange: rawData.supportsTimeRange === true,
    customFilter: rawData.customFilter === true,
  };
  if (isRecord(rawData.paramOptions)) {
    data.paramOptions = Object.fromEntries(Object.entries(rawData.paramOptions)
      .filter(([, options]) => Array.isArray(options))
      .map(([key, options]) => [key, (options as unknown[]).map(String)]));
  }
  if (isRecord(rawData.paramLabels)) data.paramLabels = Object.fromEntries(Object.entries(rawData.paramLabels).map(([key, label]) => [key, String(label)]));
  const rawPosition = isRecord(value.position) ? value.position : {};
  const x = Number(rawPosition.x);
  const y = Number(rawPosition.y);
  return {
    id: typeof value.id === 'string' ? value.id : `node-${index + 1}`,
    type,
    deletable: true,
    position: { x: Number.isFinite(x) ? x : 120 + index * 240, y: Number.isFinite(y) ? y : 200 },
    data,
  };
}

function normalizeEdge(value: unknown, index: number, nodes: WorkflowNode[]): WorkflowEdge | null {
  if (!isRecord(value) || typeof value.source !== 'string' || typeof value.target !== 'string') return null;
  if (!nodes.some((node) => node.id === value.source) || !nodes.some((node) => node.id === value.target)) return null;
  const sourceHandle = typeof value.sourceHandle === 'string' ? value.sourceHandle : undefined;
  const targetHandle = typeof value.targetHandle === 'string' ? value.targetHandle : undefined;
  const source = nodes.find((node) => node.id === value.source);
  const target = nodes.find((node) => node.id === value.target);
  const streamType = isRecord(value.data) && (value.data.streamType === 'audio' || value.data.streamType === 'video')
    ? value.data.streamType
    : sourceHandle === 'audio' || targetHandle === 'audio' || source?.data.streamType === 'audio' || target?.data.streamType === 'audio'
      ? 'audio'
      : 'video';
  return {
    id: typeof value.id === 'string' ? value.id : `edge-${index + 1}`,
    source: value.source,
    target: value.target,
    sourceHandle,
    targetHandle,
    type: typeof value.type === 'string' ? value.type : 'smoothstep',
    data: { streamType },
    style: streamEdgeStyle(streamType),
  };
}

export function normalizeWorkflow(value: unknown): WorkflowDocument | null {
  if (!isRecord(value)) return null;
  const source = isRecord(value.workflow) ? value.workflow : value;
  if (!Array.isArray(source.nodes)) return null;
  const nodes = source.nodes.map(normalizeNode).filter((node): node is WorkflowNode => !!node);
  if (!nodes.length) return null;
  const edgeValues = Array.isArray(source.edges) ? source.edges : [];
  const edges = edgeValues.map((edge, index) => normalizeEdge(edge, index, nodes)).filter((edge): edge is WorkflowEdge => !!edge);
  return { version: 3, nodes, edges };
}

export function parseWorkflowConfig(content: string): WorkflowDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content) as unknown;
  } catch {
    throw new Error('配置文件不是有效的 JSON。');
  }
  const workflow = normalizeWorkflow(parsed);
  if (!workflow) throw new Error('配置文件中没有可识别的工作流节点。');
  return workflow;
}

export function loadWorkflow(storage?: StorageLike): WorkflowDocument {
  try {
    const target = storage || window.localStorage;
    const current = target.getItem(WORKFLOW_STORAGE_KEY);
    if (current) {
      const normalized = normalizeWorkflow(JSON.parse(current) as unknown);
      if (normalized) return normalized;
    }
    const oldNodes = target.getItem(LEGACY_NODES_KEY);
    if (oldNodes) {
      const nodes = JSON.parse(oldNodes) as unknown;
      const edges = target.getItem(LEGACY_EDGES_KEY);
      const migrated = normalizeWorkflow({ nodes, edges: edges ? JSON.parse(edges) as unknown : [] });
      if (migrated) return migrated;
    }
  } catch {
    return createInitialWorkflow();
  }
  return createInitialWorkflow();
}

export function serializeWorkflow(workflow: WorkflowDocument): string {
  const cleanNodes = workflow.nodes.map((node) => ({
    id: node.id,
    type: node.type,
    position: node.position,
    data: {
      ...node.data,
      onChange: undefined,
      onDelete: undefined,
    },
  }));
  const cleanEdges = workflow.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    data: edge.data,
    type: edge.type,
  }));
  return JSON.stringify({ version: 3, nodes: cleanNodes, edges: cleanEdges }, null, 2);
}

export function saveWorkflow(workflow: WorkflowDocument, storage?: StorageLike): boolean {
  try {
    (storage || window.localStorage).setItem(WORKFLOW_STORAGE_KEY, serializeWorkflow(workflow));
    return true;
  } catch {
    return false;
  }
}
